import { Box, CircularProgress, Divider, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAvailableReports } from '../../hooks/Reports/useAvailableReports';
import { REPORT_CATEGORIES } from '../../constants/Reports';
import ReportCategorySection from '../../components/Reports/ReportCategorySection';
import AuthContext from '../../context/AuthContext';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Every report is always clickable — no lock/disabled state
const ALL_REPORT_IDS = REPORT_CATEGORIES.flatMap((c) =>
  c.reports.filter(Boolean).map((r) => r.id)
);

const Reports = () => {
  const navigate = useNavigate();
  const { isReportsLoading } = useAvailableReports();
  const { department, v3Data } = useContext(AuthContext);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const isReportVisible = (r) => !r.flagKey || !!v3Data?.[r.flagKey];

  const handleNavigate = (link) => {
    navigate(link);
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return REPORT_CATEGORIES.flatMap((c) =>
      c.reports.filter(
        (r) =>
          r &&
          isReportVisible(r) &&
          (r.title.toLowerCase().includes(q) ||
            (r.description || '').toLowerCase().includes(q))
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, v3Data]);

  return (
    <Paper
      elevation={0}
      sx={{
        marginTop: '1rem',
        borderRadius: '16px',
        border: '1px solid #EAECF0',
        overflow: 'hidden',
      }}
    >
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 4,
          py: 3,
          borderBottom: '1px solid #EAECF0',
          background: 'linear-gradient(135deg, #fff 60%, rgba(191,29,45,0.03) 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: 'rgba(191,29,45,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AssessmentOutlinedIcon sx={{ color: '#BF1D2D', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: 'karla, sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              color: '#111827',
              lineHeight: 1.2,
            }}
          >
            Reports
          </Typography>
          <Typography
            sx={{
              fontFamily: 'karla, sans-serif',
              fontSize: '13px',
              color: '#6B7280',
              mt: 0.25,
            }}
          >
            Select a category to view available reports
          </Typography>
        </Box>
      </Box>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <Box sx={{ p: 3 }}>
        {/* Search input */}
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search reports…"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'karla, sans-serif',
              backgroundColor: '#F9FAFB',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: '#BF1D2D' },
            },
          }}
        />

        {isReportsLoading ? (
          <Box
            sx={{ minHeight: '200px' }}
            className="w-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress size={32} sx={{ color: '#BF1D2D' }} />
          </Box>
        ) : searchResults ? (
          /* ── Search results ─────────────────────────────────────────── */
          <Box
            sx={{
              borderRadius: '12px',
              border: '1px solid #EAECF0',
              overflow: 'hidden',
            }}
          >
            {searchResults.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: 'karla, sans-serif',
                    fontSize: '14px',
                    color: '#9CA3AF',
                  }}
                >
                  No reports match "{searchQuery}"
                </Typography>
              </Box>
            ) : (
              searchResults.map((report, i) => (
                <Box key={report.id}>
                  {i > 0 && <Divider sx={{ mx: 3, borderColor: '#F3F4F6' }} />}
                  <SearchResultRow
                    report={report}
                    onNavigate={handleNavigate}
                  />
                </Box>
              ))
            )}
          </Box>
        ) : (
          /* ── Category accordion ─────────────────────────────────────── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {REPORT_CATEGORIES.map((category, idx) => {
              const label =
                category.label ?? (department || 'Department Reports');

              return (
                <ReportCategorySection
                  key={category.id}
                  categoryIndex={idx}
                  label={label}
                  subtitle={category.subtitle}
                  reports={category.reports.filter(Boolean).filter(isReportVisible)}
                  availableIds={ALL_REPORT_IDS}
                  onNavigate={handleNavigate}
                  open={openCategoryId === category.id}
                  onToggle={() =>
                    setOpenCategoryId((prev) =>
                      prev === category.id ? null : category.id
                    )
                  }
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const SearchResultRow = ({ report, onNavigate }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onClick={() => onNavigate(report.link)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.75,
        backgroundColor: hovered ? '#F9FAFB' : '#ffffff',
        cursor: 'pointer',
        transition: 'background-color 0.12s ease',
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'karla, sans-serif',
            fontWeight: 600,
            fontSize: '14px',
            color: '#111827',
            lineHeight: 1.3,
          }}
        >
          {report.title}
        </Typography>
        {report.description && (
          <Typography
            sx={{
              fontFamily: 'karla, sans-serif',
              fontSize: '12px',
              color: '#6B7280',
              mt: 0.3,
              lineHeight: 1.4,
            }}
          >
            {report.description}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s ease',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'karla, sans-serif',
            fontSize: '12px',
            fontWeight: 600,
            color: '#BF1D2D',
          }}
        >
          Open
        </Typography>
        <OpenInNewIcon sx={{ fontSize: 14, color: '#BF1D2D' }} />
      </Box>
    </Box>
  );
};

export default Reports;
